import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { testAllConnections } from './utils/supabaseAPI';
import { Toaster } from 'react-hot-toast';
import { useAppUpdate } from './hooks/useAppUpdate';
import { useNotifications } from './useNotifications';
import UpdatePrompt from './components/UI/UpdatePrompt';
import { initCacheManagement } from './utils/clearCache';
import Layout from './components/Common/Layout';
import PushNotificationPrompt from './components/UI/PushNotificationPrompt';
// import PWAInstallPrompt from './components/UI/PWAInstallPrompt';
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import CreateAccount from './components/Auth/CreateAccount';
import PendingApproval from './components/Auth/PendingApproval';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import ClientDashboard from './components/Client/Dashboard';
import AdminDashboard from './components/Admin/AdminDashboard';
import LoanRequests from './components/Admin/LoanRequests';
import UserManagement from './components/Admin/UserManagement';
import Analytics from './components/Admin/Analytics';
import AdminMenu from './components/Admin/AdminMenu';
import Settings from './components/Admin/Settings';
import AdminABEpargne from './components/Admin/ABEpargne';
import TestNotifications from './components/Admin/TestNotifications';
import Transactions from './components/Admin/Transactions';
import LoanRequest from './components/Client/LoanRequest';
import LoanHistory from './components/Client/LoanHistory';
import Repayment from './components/Client/Repayment';
import RepaymentSuccess from './components/Client/RepaymentSuccess';
import RepaymentFailure from './components/Client/RepaymentFailure';
import RepaymentCancel from './components/Client/RepaymentCancel';
import Profile from './components/Client/Profile';
import Menu from './components/Client/Menu';
import ABLogement from './components/Client/ABLogement';
import ABEpargne from './components/Client/ABEpargne';
import EpargneHistorique from './components/Client/EpargneHistorique';
import RetourEpargne from './components/Client/RetourEpargne';
import PlanEpargne from './components/Client/PlanEpargne';
import PersonalizePlan from './components/Client/PersonalizePlan';
import DepotRetour from './components/Client/DepotRetour';
import RemboursementRetour from './components/Client/RemboursementRetour';
import TestPlanEpargne from './components/Client/TestPlanEpargne';
import CoachingFinance from './components/Client/CoachingFinance';
import LoyaltyScore from './components/Client/LoyaltyScore';
import TestOTP from './components/TestOTP';
import TestHealth from './components/TestHealth';
import TestFedaPay from './components/TestFedaPay';
import './styles/globals.css';

// Imports optionnels - chargés de manière asynchrone pour ne pas bloquer
// Ces fichiers sont des utilitaires de test/dev, pas critiques pour le fonctionnement
setTimeout(() => {
  try {
    require('./utils/testPWA');
  } catch (e) {
    // Ignorer silencieusement si le fichier n'existe pas ou a une erreur
  }
}, 100);

setTimeout(() => {
  try {
    require('./utils/testNotificationPrompt');
  } catch (e) {
    // Ignorer silencieusement
  }
}, 100);

setTimeout(() => {
  try {
    require('./utils/resetUserPrompts');
  } catch (e) {
    // Ignorer silencieusement
  }
}, 100);

const AdminDetailedStatistics = lazy(() => import('./components/Admin/AdminDetailedStatistics'));

// Composant de notification de mise à jour

// Composant de configuration pour Supabase manquant
const ConfigurationPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">AB Campus Finance</h1>
          <div className="w-24 h-24 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mx-auto flex items-center justify-center">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>
        
        <div className="space-y-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-800 mb-2">Configuration requise</h3>
            <p className="text-yellow-700">
              L'application nécessite une configuration Supabase pour fonctionner.
            </p>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <h4 className="font-semibold text-gray-800 mb-2">Étapes de configuration :</h4>
            <ol className="list-decimal list-inside space-y-2 text-gray-600 text-sm">
              <li>Créez un projet sur <a href="https://supabase.com" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Supabase.com</a></li>
              <li>Copiez l'URL et la clé anonyme depuis Settings → API</li>
              <li>Créez un fichier <code className="bg-gray-200 px-1 rounded">.env.local</code> à la racine du projet</li>
              <li>Ajoutez vos clés Supabase</li>
              <li>Redémarrez l'application</li>
            </ol>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-800 mb-2">Exemple de fichier .env.local :</h4>
            <pre className="text-xs text-blue-700 bg-blue-100 p-3 rounded overflow-x-auto">
{`REACT_APP_SUPABASE_URL=https://your-project.supabase.co
REACT_APP_SUPABASE_ANON_KEY=your_anon_key_here
REACT_APP_BACKEND_URL=http://localhost:5000`}
            </pre>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-800 mb-2">Diagnostic de la configuration :</h4>
            <div className="text-sm text-red-700 space-y-1">
              <p>• URL Supabase: {process.env.REACT_APP_SUPABASE_URL ? '✅ Définie' : '❌ Manquante'}</p>
              <p>• Clé Supabase: {process.env.REACT_APP_SUPABASE_ANON_KEY ? '✅ Définie' : '❌ Manquante'}</p>
              <p>• Backend URL: {process.env.REACT_APP_BACKEND_URL ? '✅ Définie' : '❌ Manquante'}</p>
              <p>• Mode: {process.env.NODE_ENV || 'Non défini'}</p>
            </div>
            <p className="text-xs text-red-600 mt-2">
              💡 Ouvrez la console du navigateur (F12) pour voir les logs détaillés
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                console.log('[CONFIG] Tentative de rechargement...');
                console.log('[CONFIG] Variables d\'environnement:', {
                  hasUrl: !!process.env.REACT_APP_SUPABASE_URL,
                  hasKey: !!process.env.REACT_APP_SUPABASE_ANON_KEY,
                  url: process.env.REACT_APP_SUPABASE_URL ? 'Définie' : 'Manquante',
                  key: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Définie' : 'Manquante'
                });
                window.location.reload();
              }} 
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Recharger après configuration
            </button>
            
            <button 
              onClick={() => {
                console.log('[CONFIG] Test de connexion manuel...');
                testAllConnections().then(result => {
                  console.log('[CONFIG] Résultat du test:', result);
                  alert(`Test de configuration: ${result.success ? 'SUCCÈS' : 'ÉCHEC'}\nDétails: ${result.error || result.message || 'Aucun détail'}`);
                }).catch(error => {
                  console.error('[CONFIG] Erreur du test:', error);
                  alert(`Erreur du test: ${error.message}`);
                });
              }} 
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Tester la configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour protéger les routes
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Composant principal de l'application
const AppContent = () => {
  const {
    updateAvailable,
    currentVersion,
    newVersion,
    applyUpdate,
    ignoreUpdate
  } = useAppUpdate();
  const { token, notification } = useNotifications();

  return (
    <>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/create-account" element={<CreateAccount />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout>
                <ClientDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <AdminDashboard />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/loan-requests" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <LoanRequests />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/users" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <UserManagement />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/user-management" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <UserManagement />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/menu" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <AdminMenu />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/analytics" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <Analytics />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics/detailed" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <Suspense fallback={
                  <div className="min-h-screen flex items-center justify-center bg-green-50">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-green-500 border-t-transparent" />
                  </div>
                }>
                  <AdminDetailedStatistics />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <Settings />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/ab-epargne" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <AdminABEpargne />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/test-notifications" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <TestNotifications />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/admin/transactions" element={
            <ProtectedRoute adminOnly>
              <Layout>
                <Transactions />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/loan-request" element={
            <ProtectedRoute>
              <Layout>
                <LoanRequest />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/loan-history" element={
            <ProtectedRoute>
              <Layout>
                <LoanHistory />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/repayment" element={
            <ProtectedRoute>
              <Layout>
                <Repayment />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/repayment/success" element={
            <ProtectedRoute>
              <Layout>
                <RepaymentSuccess />
              </Layout>
            </ProtectedRoute>
          } />
          {/* URL utilisée par le backend après redirection FedaPay — même page qui met à jour le prêt en "completed" */}
          <Route path="/remboursement/success" element={
            <ProtectedRoute>
              <Layout>
                <RepaymentSuccess />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/repayment/failure" element={
            <ProtectedRoute>
              <Layout>
                <RepaymentFailure />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/remboursement/failure" element={
            <ProtectedRoute>
              <Layout>
                <RepaymentFailure />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/repayment/cancel" element={
            <ProtectedRoute>
              <Layout>
                <RepaymentCancel />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/remboursement/cancel" element={
            <ProtectedRoute>
              <Layout>
                <RepaymentCancel />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/menu" element={
            <ProtectedRoute>
              <Layout>
                <Menu />
              </Layout>
            </ProtectedRoute>
          } />
          
          
          <Route path="/ab-logement" element={
            <ProtectedRoute>
              <Layout>
                <ABLogement />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/ab-epargne" element={
            <ProtectedRoute>
              <Layout>
                <ABEpargne />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/ab-epargne/historique" element={
            <ProtectedRoute>
              <Layout>
                <EpargneHistorique />
              </Layout>
            </ProtectedRoute>
          } />
          <Route path="/ab-epargne/retour" element={
            <ProtectedRoute>
              <Layout>
                <RetourEpargne />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/ab-epargne/personalize/:planId" element={
            <ProtectedRoute>
              <Layout>
                <PersonalizePlan />
              </Layout>
            </ProtectedRoute>
          } />
          
            <Route path="/ab-epargne/plan/:id" element={
              <ProtectedRoute>
                <Layout>
                  <PlanEpargne />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/ab-epargne/depot-retour" element={
              <ProtectedRoute>
                <Layout>
                  <DepotRetour />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/remboursement-retour" element={
              <ProtectedRoute>
                <Layout>
                  <RemboursementRetour />
                </Layout>
              </ProtectedRoute>
            } />
          
          <Route path="/coaching-finance" element={
            <ProtectedRoute>
              <Layout>
                <CoachingFinance />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/loyalty-score" element={
            <ProtectedRoute>
              <Layout>
                <LoyaltyScore />
              </Layout>
            </ProtectedRoute>
          } />
          
          <Route path="/test-otp" element={<TestOTP />} />
          <Route path="/test-health" element={<TestHealth />} />
          <Route path="/test-fedapay" element={<TestFedaPay />} />
          <Route path="/test-plan-epargne/:id" element={<TestPlanEpargne />} />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Router>

      {/* Prompt de mise à jour élégant */}
      <UpdatePrompt
        updateAvailable={updateAvailable}
        newVersion={newVersion}
        currentVersion={currentVersion}
        onUpdate={applyUpdate}
        onIgnore={ignoreUpdate}
      />
    </>
  );
};

// Composant principal avec les providers
const App = () => {
  const [isConfigured, setIsConfigured] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkConfiguration = () => {
      try {
        console.log('[APP] Vérification des variables d\'environnement...');
        
        // Vérification simple des variables d'environnement
        const hasSupabaseUrl = !!process.env.REACT_APP_SUPABASE_URL;
        const hasSupabaseKey = !!process.env.REACT_APP_SUPABASE_ANON_KEY;
        const hasBackendUrl = !!process.env.REACT_APP_BACKEND_URL;
        
        console.log('[APP] Variables d\'environnement:', {
          hasSupabaseUrl,
          hasSupabaseKey,
          hasBackendUrl,
          url: process.env.REACT_APP_SUPABASE_URL ? 'Définie' : 'Manquante',
          key: process.env.REACT_APP_SUPABASE_ANON_KEY ? 'Définie' : 'Manquante',
          backend: process.env.REACT_APP_BACKEND_URL ? 'Définie' : 'Manquante'
        });
        
        // Configuration OK si toutes les variables sont présentes
        const configured = hasSupabaseUrl && hasSupabaseKey && hasBackendUrl;
        setIsConfigured(configured);
        
        if (configured) {
          console.log('[APP] ✅ Configuration détectée, démarrage de l\'application');
        } else {
          console.error('[APP] ❌ Configuration manquante, affichage de la page de configuration');
          console.error('[APP] Variables manquantes:', {
            url: !hasSupabaseUrl,
            key: !hasSupabaseKey,
            backend: !hasBackendUrl
          });
        }
      } catch (error) {
        console.error('[APP] ❌ Erreur lors de la vérification de la configuration:', error);
        console.error('[APP] Stack:', error.stack);
        setIsConfigured(false);
      } finally {
        setIsChecking(false);
      }
    };

    // Délai minimal pour iOS pour s'assurer que tout est chargé
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const delay = isIOS ? 100 : 0;
    
    setTimeout(() => {
      checkConfiguration();
      
      // Initialiser la gestion du cache après vérification
      try {
        console.log('[APP] Initialisation de la gestion du cache...');
        initCacheManagement();
      } catch (error) {
        console.error('[APP] Erreur lors de l\'initialisation du cache:', error);
        // Ne pas bloquer l'application si le cache échoue
      }
    }, delay);
  }, []);

  // Afficher un loader pendant la vérification
  if (isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de la configuration...</p>
        </div>
      </div>
    );
  }

  if (!isConfigured) {
    return <ConfigurationPage />;
  }

  return (
    <AuthProvider>
      <NotificationProvider>
        <AppContent />
        <PushNotificationPrompt />
        {/* <PWAInstallPrompt /> */}
        <Toaster position="top-right" />
      </NotificationProvider>
    </AuthProvider>
  );
};

export default App;